// Track finder (RC Car Action) editorial client: the review queue and track editing.
import { api } from './client'

export type TrackStatus = 'listed' | 'closed' | 'hidden'

export interface TrackRow {
  id: number
  name: string
  address: string | null
  city: string | null
  state: string
  zip: string | null
  lat: number | null
  lng: number | null
  types: string[]
  setting: string | null
  surfaces: string[]
  website: string | null
  social_url: string | null
  phone: string | null
  status: TrackStatus
  verified_at: string | null
  verified_by: number | null
  last_raced_at: string | null
}

export interface QueueItem {
  id: number
  kind: string
  created_at: string
  age_days: number
  overdue: boolean
  track: TrackRow | null
  other_track: TrackRow | null
  reason: string | null
  note: string | null
  /** Reader-supplied; contact keys are stripped for writers. Render as text only. */
  payload: Record<string, unknown> | null
  reader_email: string | null
  resolved_at: string | null
  resolution: string | null
}

export interface QueueResponse {
  items: QueueItem[]
  open: number
  overdue: number
}

export interface ResolveBody {
  action: 'approve' | 'verify' | 'close' | 'merge' | 'dismiss'
  merge_into?: number
  fields?: Record<string, unknown>
}

export interface TrackList {
  items: TrackRow[]
  total: number
  page: number
  page_size: number
}

export type TrackFields = Partial<Omit<TrackRow, 'id' | 'verified_at' | 'verified_by' | 'last_raced_at'>>

const BASE = '/api/editorial/tracks'

export const getQueue = (includeResolved = false) =>
  api.get<QueueResponse>(`${BASE}/queue?include_resolved=${includeResolved}`)

export const resolve = (id: number, body: ResolveBody) =>
  api.post<{ id: number; resolution: string; track_id: number | null }>(`${BASE}/queue/${id}/resolve`, body)

export const listTracks = (params: { q?: string; state?: string; status?: string; page?: number } = {}) => {
  const p = new URLSearchParams()
  if (params.q) p.set('q', params.q)
  if (params.state) p.set('state', params.state)
  if (params.status) p.set('status', params.status)
  if (params.page) p.set('page', String(params.page))
  return api.get<TrackList>(`${BASE}/tracks?${p}`)
}

export const createTrack = (body: TrackFields & { name: string; state: string }) =>
  api.post<TrackRow>(`${BASE}/tracks`, body)

export const patchTrack = (id: number, body: TrackFields) => api.patch<TrackRow>(`${BASE}/tracks/${id}`, body)

export const verifyTrack = (id: number, fields?: TrackFields) =>
  api.post<TrackRow>(`${BASE}/tracks/${id}/verify`, fields ? { fields } : undefined)
